/**
 * Patient PAP — field-level effects.
 *
 * Migrated from: patient/patient-pap-base.coffee
 *
 * Old validators → new effects:
 *   PAPValidate            → PAPValidate
 *   PAPStateValidate       → PAPStateValidate
 *   ShortNameValidate      → ShortNameValidate
 *   PAPSubmitDateValidate  → PAPSubmitDateValidate
 *   PAPRenewalDataValidate → PAPRenewalDataValidate
 *   StatusChange           → PAPStatusChange
 *   CheckCondition         → PAPCheckCondition
 *   CheckForAlert          → PAPCheckForAlert
 *   PAPExpiryAlert         → PAPExpiryAlert
 *   PAPBridgeAltGate       → deny-alt flag (Yes while Denied)
 *
 * Skipped:
 *   handle(dfd) init logic (prefill_refferal, check_for_alert, check_condition, status_change) — form-load
 *   clear_requirements / add_requirements — internal helpers for PAPValidate
 *   pap_program_alert — internal helper for PAPValidate
 */
import dayjs from "dayjs";

import { buildApiUrl, fetchAPI } from "@neu/libui";
import type { FieldHandler } from "@neu/libui";

import { syncLocalBiosimilarPreview } from "../shared/sync-local-biosimilar-preview";

import type { Form } from "@formily/core";

// ─── PAPValidate ────────────────────────────────────────────────────────────
// Old: DSLFx.Validators.PAPValidate
// Trigger field: program_id
// Fetches drug_brand_short data, sets program_name, and manages requirements subform.
const setPAPRequirements = async (form: Form, programId: number) => {
	if (!programId) {
		return;
	}
	const data = await fetchAPI(buildApiUrl(`/pap_program/${programId}`));
	const pap = Array.isArray(data) ? data[0] : data;
	form.setFieldState("requirements", (s) => {
		const v = (
			(s.value ?? []) as Array<{
				requirement: string;
				value?: string;
				id?: string;
				deleted?: boolean;
			}>
		).map((v) => {
			v.deleted = true;
			return v;
		});
		(pap.requirements ?? []).forEach((r: any) => {
			let isHit = false;
			for (const i of v) {
				if ((i.requirement = r.name)) {
					i.deleted = false;
					isHit = true;
				}
			}
			if (!isHit) {
				v.push({
					requirement: r.name,
				});
			}
		});

		s.value = [...v];
	});
};
export const PAPValidate: FieldHandler = (field, { form }) => {
	void (async () => {
		const programId = field.value?.id;
		if (!programId) return;

		const lastProgramId = form.getFieldState("last_program_id")?.value;
		if (lastProgramId && parseInt(lastProgramId) === parseInt(programId)) {
			return;
		}

		// Alert for specific programs
		const alertPaps: Record<number, string> = {
			188: "Praluent is for the uninsured patients only.",
		};
		if (parseInt(programId) in alertPaps) {
			field.setFeedback({
				type: "warning",
				messages: [alertPaps[parseInt(programId)]],
			});
		} else {
			field.setFeedback({
				type: "warning",
				messages: [],
			});
		}

		try {
			form.setFieldState("last_program_id", (s) => {
				s.value = programId;
			});
			const data = await fetchAPI(
				buildApiUrl(
					`/drug_brand_short/list?limit=1&_drug_pap=${programId}`
				)
			);
			const brands = Array.isArray(data) ? data : (data?.data ?? []);
			const [brand] = brands;
			if (!brand) return;
			form.setFieldState("program_name", (s) => {
				s.value = brand.name;
			});
			await setPAPRequirements(form, programId as number);
		} catch (err) {
			console.error("PAPValidate: API error", err);
		}
	})();
	return "";
};

// ─── PAPStateValidate ───────────────────────────────────────────────────────
// Old: DSLFx.Validators.PAPStateValidate
// Trigger field: status (or pap_form_received, pap_form_sent_dr, pap_form_submitted)
// Validates PAP form completeness before allowing status change from Pending.
export const PAPStateValidate: FieldHandler = (field, { form }) => {
	const status = form.getFieldState("status")?.value;
	if (!status || status === "Pending") return "";

	const papReceivedDate = form.getFieldState("pap_received_date")?.value;
	const papFaxDate = form.getFieldState("pap_fax_date")?.value;
	const submitDate = form.getFieldState("submit_date")?.value;
	const papFormReceived = form.getFieldState("pap_form_received")?.value;
	const papFormSentDr = form.getFieldState("pap_form_sent_dr")?.value;
	const papFormSubmitted = form.getFieldState("pap_form_submitted")?.value;

	const missing: string[] = [];

	if (papFormReceived !== "Yes" || !papReceivedDate) {
		missing.push("Patient signature date");
	}
	if (papFormSentDr !== "Yes" || !papFaxDate) {
		missing.push("Physician signature date");
	}
	if (papFormSubmitted !== "Yes" || !submitDate) {
		missing.push("Submission date");
	}

	if (missing.length > 0) {
		field.setFeedback({
			type: "error",
			messages: [
				`The case is missing the following information: ${missing.join(", ")}`,
			],
		});
		form.setFieldState("status", (s) => {
			s.value = "Pending";
		});
	} else {
		field.setFeedback({ type: "error", messages: [] });
	}
	return "";
};

// ─── ShortNameValidate ──────────────────────────────────────────────────────
// Old: DSLFx.Validators.ShortNameValidate
// Trigger field: (triggers on PAP form context)
// Sets short_name based on the case's medication name and medical_retail type.
export const ShortNameValidate: FieldHandler = (
	field,
	{ form, parentForm }
) => {
	const shortName = parentForm?.getFieldState("medication_short_name")?.value;
	const medicalRetail = parentForm?.getFieldState("medical_retail")?.value;

	if (!shortName) return "";

	const lowerName = shortName.toLowerCase();

	if (lowerName === "humira") {
		form.setFieldState("short_name", (s) => {
			s.value = "abbvie";
		});
	} else if (
		["prolia", "enbrel", "corlanor", "repatha", "sensipar"].includes(
			lowerName
		) &&
		medicalRetail?.toLowerCase() === "pharmacy"
	) {
		form.setFieldState("medical_retail", (s) => {
			s.value = "Pharmacy";
		});
		form.setFieldState("short_name", (s) => {
			s.value = "amgen";
		});
	} else if (
		["prolia"].includes(lowerName) &&
		medicalRetail?.toLowerCase() === "medical"
	) {
		form.setFieldState("short_name", (s) => {
			s.value = "amgen";
		});
	} else {
		form.setFieldState("medical_retail", (s) => {
			s.value = "";
		});
		form.setFieldState("short_name", (s) => {
			s.value = "";
		});
	}
	return "";
};

// ─── PAPSubmitDateValidate ──────────────────────────────────────────────────
// Old: DSLFx.Validators.PAPSubmitDateValidate
// Trigger field: pap_form_submitted
// Defaults pap_sent_date to today if not already set.
export const PAPSubmitDateValidate: FieldHandler = (field, { form }) => {
	const papSentDate = form.getFieldState("pap_sent_date")?.value;
	if (!papSentDate) {
		form.setFieldState("pap_sent_date", (s) => {
			s.value = new Date();
		});
	}
	return "";
};

// ─── PAPRenewalDataValidate ─────────────────────────────────────────────────
// Old: DSLFx.Validators.PAPRenewalDataValidate
// Trigger field: patient_id (or renewal-related field)
// Clears the PAP enrollment date field when status is Pending.
export const PAPRenewalDataValidate: FieldHandler = (field, { form }) => {
	const status = form.getFieldState("status")?.value;
	if (status === "Pending") {
		form.setFieldState("date", (s) => {
			s.value = undefined;
		});
	}
	return "";
};

const DENY_ALT_FIELDS = [
	"is_biosimilar_available",
	"biosimilar_brand_offered",
	"biosimilar_drug_id",
	"is_bridge_available",
	"start_bridge_app",
	"bridge_program",
	"is_copay_available",
	"start_copay_app",
	"copay_program",
	"is_340b_available",
	"start_340b_app",
	"is_provider_available",
] as const;

function clearPapDenyAlternatives(form: Form) {
	form.setFieldState("show_post_bridge_alts", (s) => {
		s.value = "No";
	});
	for (const name of DENY_ALT_FIELDS) {
		form.setFieldState(name, (s) => {
			s.value = undefined;
		});
	}
}

/** Keeps case `subform_biosimilar` in sync for UI before case Save (local only). */
export const PAPSyncLocalBiosimilarPreview: FieldHandler = (field, props) => {
	syncLocalBiosimilarPreview({
		form: props.form,
		parentForm: props.parentForm,
		createFrom: "Patient PAP Form",
	});
	return "";
};

// ─── PAPBiosimilarBrandChange ───────────────────────────────────────────────
// Clears Biosimilar Drugs when Brands Offered for Biosimilar changes (user edit only).
export const PAPBiosimilarBrandChange: FieldHandler = (field, props) => {
	const { form } = props;
	const modified = Boolean(
		(field as { modified?: boolean; selfModified?: boolean }).modified ||
		(field as { modified?: boolean; selfModified?: boolean }).selfModified
	);
	if (modified) {
		form.setFieldState("biosimilar_drug_id", (s) => {
			s.value = undefined;
		});
	}
	PAPSyncLocalBiosimilarPreview(field, props);
	return "";
};

// ─── PAPBridgeAltGate ───────────────────────────────────────────────────────
// Tracks deny-alt visibility flag: Yes while status is Denied, otherwise No.
export const PAPBridgeAltGate: FieldHandler = (field, { form }) => {
	const status = form.getFieldState("status")?.value ?? field.value;
	form.setFieldState("show_post_bridge_alts", (s) => {
		s.value = status === "Denied" ? "Yes" : "No";
	});
	return "";
};

// ─── PAPStatusChange ────────────────────────────────────────────────────────
// Old: DSLFx.Validators.StatusChange (patient_pap version)
// Trigger field: status
// When PAP status is Denied, looks up Bridge / Copay / 340B availability.
// When status leaves Denied, clears deny-alternative fields so they hide cleanly.
let papStatusChangeSeq = 0;

export const PAPStatusChange: FieldHandler = (field, props) => {
	const { form, ...rest } = props;
	const status = form.getFieldState("status")?.value ?? field.value;

	if (status !== "Denied") {
		papStatusChangeSeq += 1;
		clearPapDenyAlternatives(form);
		PAPSyncLocalBiosimilarPreview(field, props);
		return "";
	}

	const seq = ++papStatusChangeSeq;

	void (async () => {
		const medId =
			rest.parentForm?.getFieldState("medication_id")?.value?.id;

		if (!medId) return;

		try {
			const invData = await fetchAPI(buildApiUrl(`/inventory/${medId}`));
			if (seq !== papStatusChangeSeq) return;
			if (form.getFieldState("status")?.value !== "Denied") return;

			const inv = Array.isArray(invData) ? invData[0] : invData;
			if (!inv.brand_name_id?.id) return;

			const brandData = await fetchAPI(
				buildApiUrl(`/drug_brand_short/${inv.brand_name_id?.id}`)
			);
			if (seq !== papStatusChangeSeq) return;
			if (form.getFieldState("status")?.value !== "Denied") return;

			const brand = Array.isArray(brandData) ? brandData[0] : brandData;
			if (!brand) return;

			const isBridgeFound = brand.bridge_program_available || "No";
			const isCopayFound = brand.copay_available || "No";
			const is340bFound = brand.discount_drug_program_available || "No";

			form.setFieldState("is_bridge_available", (s) => {
				s.value = isBridgeFound;
			});
			form.setFieldState("is_copay_available", (s) => {
				s.value = isCopayFound;
			});
			form.setFieldState("is_340b_available", (s) => {
				s.value = is340bFound;
			});

			if (Array.isArray(brand.drug_bridge) && brand.drug_bridge.length) {
				const bridgeOptions = brand.drug_bridge.map((record: any) => ({
					value: record.id,
					label: record.name,
				}));
				form.setFieldState("bridge_program", (s) => {
					s.dataSource = bridgeOptions;
				});
			}

			if (Array.isArray(brand.drug_copay) && brand.drug_copay.length) {
				const copayOptions = brand.drug_copay.map((record: any) => ({
					value: record.id,
					label: record.name,
				}));
				form.setFieldState("copay_program", (s) => {
					s.dataSource = copayOptions;
				});
			}

			PAPBridgeAltGate(field, props);
			PAPSyncLocalBiosimilarPreview(field, props);
		} catch (err) {
			console.error("PAPStatusChange: API error", err);
		}
	})();
	PAPSyncLocalBiosimilarPreview(field, props);
	return "";
};

// ─── PAPCheckCondition ──────────────────────────────────────────────────────
// Old: DSLFx.Validators.CheckCondition (patient_pap version)
// Trigger field: start_copay_app / is_340b_available
// Sets condition_chck = "Yes" when 340B available and copay not started.
export const PAPCheckCondition: FieldHandler = (field, { form }) => {
	const startCopayApp = form.getFieldState("start_copay_app")?.value;
	const is340bAvailable = form.getFieldState("is_340b_available")?.value;

	form.setFieldState("condition_chck", (s) => {
		s.value = "No";
	});

	if (is340bAvailable === "Yes" && startCopayApp !== "Yes") {
		form.setFieldState("condition_chck", (s) => {
			s.value = "Yes";
		});
	}
	return "";
};

// ─── PAPCheckForAlert ───────────────────────────────────────────────────────
// Old: DSLFx.Validators.CheckForAlert (patient_pap version)
// Trigger field: start_copay_app / start_340b_app / is_copay_available / is_340b_available
// Sets alert messages for copay/340B program availability.
export const PAPCheckForAlert: FieldHandler = (field, { form }) => {
	form.setFieldState("show_alert", (s) => {
		s.value = "No";
	});

	const startCopayApp = form.getFieldState("start_copay_app")?.value;
	const start340bApp = form.getFieldState("start_340b_app")?.value;
	const isCopayAvailable = form.getFieldState("is_copay_available")?.value;
	const is340bAvailable = form.getFieldState("is_340b_available")?.value;

	if (startCopayApp === "Yes") {
		form.setFieldState("show_alert", (s) => {
			s.value = "Yes";
		});
		form.setFieldState("alert", (s) => {
			s.value = "Copay added to the case";
		});
	}

	if (start340bApp === "Yes") {
		form.setFieldState("show_alert", (s) => {
			s.value = "Yes";
		});
		form.setFieldState("alert", (s) => {
			s.value = "340B/discount drug program will added to the case";
		});
	}

	if (isCopayAvailable === "No" && is340bAvailable === "No") {
		form.setFieldState("show_alert", (s) => {
			s.value = "Yes";
		});
		form.setFieldState("alert", (s) => {
			s.value =
				"Drug has no copay or 340B options available, you will need to communicate with the member regarding other options";
		});
	}

	return "";
};

// ─── PAPExpiryAlert ─────────────────────────────────────────────────────────
// Old: DSLFx.Validators.PAPExpiryAlert
// Trigger field: renewal_date
// Calculates the PAP expiry alert date as 7 days before the renewal date.
export const PAPExpiryAlert: FieldHandler = (field, { form }) => {
	const renewalDate = form.getFieldState("renewal_date")?.value;
	if (!renewalDate) return "";

	const expiryDate = dayjs(renewalDate).subtract(7, "day").toDate();

	form.setFieldState("pap_expiry_date_alert", (s) => {
		s.value = expiryDate;
	});
	return "";
};
